import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountTree as IdpIcon,
  CheckCircle as CheckIcon,
  CloudDownload as FetchIcon,
  ContentCopy as CopyIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  Info as InfoIcon,
  Link as LinkIcon,
  Save as SaveIcon,
  Security as SecurityIcon,
  Shield as ShieldIcon,
  Tune as TuneIcon,
  VpnKey as KeyIcon,
} from '@mui/icons-material';
import { enterpriseSamlLoginAPI, ssoAPI, type EnterpriseSamlLoginConfigDTO } from '../services/api';
import { UI_TOKENS } from '../constants/uiTokens';

type SetupMode = 'metadata' | 'credentials' | 'manual';

const emptyForm = (): EnterpriseSamlLoginConfigDTO => ({
  enterprise_id: '',
  sp_entity_id: '',
  sp_acs_url: '',
  idp_metadata_url: '',
  idp_entity_id: '',
  idp_x509_cert: '',
  idp_x509_cert_extra: '',
  idp_sso_login_url: '',
  frontend_redirect_url: '',
  accepted_clock_skew_ms: 5000,
  want_assertions_signed: true,
  want_response_signed: true,
  updated_at: '',
  updated_by: null,
  updated_by_name: null,
});

function parseEntityIdFromMetadata(xml: string): string | null {
  const m = xml.match(/entityID\s*=\s*"([^"]+)"/i);
  return m ? m[1] : null;
}

function parseCertFromMetadata(xml: string): string | null {
  const m =
    xml.match(/<ds:X509Certificate[^>]*>([\s\S]+?)<\/ds:X509Certificate>/i) ||
    xml.match(/<X509Certificate[^>]*>([\s\S]+?)<\/X509Certificate>/i);
  if (!m) return null;
  const body = m[1].replace(/\s+/g, '');
  return `-----BEGIN CERTIFICATE-----\n${body}\n-----END CERTIFICATE-----`;
}

function parseSsoLoginUrlFromMetadata(xml: string): string | null {
  const serviceRegex = /<(?:[\w-]+:)?SingleSignOnService\b([^>]*)\/?>/gi;
  const services: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = serviceRegex.exec(xml)) !== null) {
    services.push(match[1]);
  }
  for (const bindingKey of ['HTTP-Redirect', 'HTTP-POST']) {
    for (const attrs of services) {
      if (!/Binding="/i.test(attrs) || !attrs.includes(bindingKey)) continue;
      const loc = attrs.match(/Location="([^"]+)"/i)?.[1]?.trim();
      if (loc) return sanitizeIdpSignInUrl(loc);
    }
  }
  for (const attrs of services) {
    const loc = attrs.match(/Location="([^"]+)"/i)?.[1]?.trim();
    if (loc) return sanitizeIdpSignInUrl(loc);
  }
  return null;
}

/** Azure /saml2 endpoints need SP-initiated login — not usable as a direct browser link. */
function isBareAzureSamlEndpoint(url: string | null | undefined): boolean {
  if (!url?.trim()) return false;
  return /login\.microsoftonline\.com\/[^/?#]+\/saml2\/?(?:\?|$)/i.test(url.trim());
}

function sanitizeIdpSignInUrl(url: string | null | undefined): string {
  const trimmed = url?.trim() || '';
  if (!trimmed || isBareAzureSamlEndpoint(trimmed)) return '';
  return trimmed;
}

function inferSetupMode(row: EnterpriseSamlLoginConfigDTO | null): SetupMode {
  if (!row) return 'metadata';
  if (row.idp_metadata_url?.trim()) return 'metadata';
  if (row.idp_entity_id?.trim() && row.idp_x509_cert?.trim()) return 'manual';
  return 'metadata';
}

const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, right, children }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider' }}>
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ mb: 1.75 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.50',
            color: 'primary.main',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
          {subtitle && (
            <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {right}
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const SamlLoginSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [form, setForm] = useState<EnterpriseSamlLoginConfigDTO>(emptyForm());
  const [savedForm, setSavedForm] = useState<string | null>(null);
  const [setupMode, setSetupMode] = useState<SetupMode>('metadata');
  const [fetching, setFetching] = useState(false);
  const [applyingCredentials, setApplyingCredentials] = useState(false);
  const [fetched, setFetched] = useState<{ entityId: string | null; cert: string | null } | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [credClientId, setCredClientId] = useState('');
  const [credClientSecret, setCredClientSecret] = useState('');
  const [credFieldsUnlocked, setCredFieldsUnlocked] = useState(false);

  const frontendOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const tenantBase = frontendOrigin ? frontendOrigin.replace(/\/$/, '') : '';
  const defaultSpEntityId = tenantBase ? `${tenantBase}/saml/sp` : '';
  const defaultAcsUrl = tenantBase ? `${tenantBase}/api/v1/auth/saml/acs` : '';
  const defaultSpLoginUrl = tenantBase ? `${tenantBase}/api/v1/auth/saml/login` : '';

  /** Stale ACS from old Azure hostnames (e.g. missing "2") breaks Sign in with SSO. */
  const isStaleAcsHost = (acs?: string | null): boolean => {
    const u = (acs || '').trim().toLowerCase();
    if (!u || !defaultAcsUrl) return false;
    if (u === defaultAcsUrl.toLowerCase()) return false;
    // Prefer current tenant URL (custom domain) over any azurewebsites ACS.
    if (u.includes('azurewebsites.net') && !defaultAcsUrl.toLowerCase().includes('azurewebsites.net')) {
      return true;
    }
    // Known dead / wrong App Service names for this tenant
    if (/turboiam-provysn-dev-be\.azurewebsites\.net/i.test(u)) return true;
    return false;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setMessage(null);
      try {
        const row = await enterpriseSamlLoginAPI.get();
        if (cancelled) return;
        if (row) {
          const rawAcs = row.sp_acs_url?.trim() || '';
          const acs = !rawAcs || isStaleAcsHost(rawAcs) ? defaultAcsUrl : rawAcs;
          const normalized: EnterpriseSamlLoginConfigDTO = {
            ...row,
            // Always bind SP Entity ID + ACS to the URL the user is on (login / Okta must match).
            sp_entity_id: defaultSpEntityId,
            sp_acs_url: acs || defaultAcsUrl,
            idp_metadata_url: row.idp_metadata_url ?? '',
            idp_entity_id: row.idp_entity_id ?? '',
            idp_x509_cert: row.idp_x509_cert ?? '',
            idp_x509_cert_extra: row.idp_x509_cert_extra ?? '',
            idp_sso_login_url: sanitizeIdpSignInUrl(row.idp_sso_login_url),
            // Always return users to this tenant after ACS (not a shared azurewebsites FE).
            frontend_redirect_url: tenantBase || row.frontend_redirect_url || '',
          };
          setForm(normalized);
          setSavedForm(JSON.stringify(normalized));
          setSetupMode(inferSetupMode(normalized));
          if (!rawAcs || isStaleAcsHost(rawAcs)) {
            setMessage({
              type: 'error',
              text:
                'ACS URL was pointing at an old/unreachable Azure host. It has been reset to your current tenant URL. ' +
                'Click Save, then update the same ACS URL in Okta (or your IdP) or Sign in with SSO will keep failing.',
            });
          }
        } else {
          const blank = {
            ...emptyForm(),
            sp_entity_id: defaultSpEntityId,
            sp_acs_url: defaultAcsUrl,
          };
          setForm(blank);
          setSavedForm(JSON.stringify(blank));
        }
      } catch {
        if (!cancelled) setMessage({ type: 'error', text: 'Could not load SSO settings.' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [defaultAcsUrl, defaultSpEntityId]);

  useEffect(() => {
    if (setupMode !== 'credentials') return;
    setCredClientId('');
    setCredClientSecret('');
    setCredFieldsUnlocked(false);
  }, [setupMode]);

  const idpReady = useMemo(() => {
    if (setupMode === 'metadata') return Boolean(form.idp_metadata_url?.trim());
    if (setupMode === 'credentials') {
      return Boolean(form.idp_metadata_url?.trim() || (form.idp_entity_id?.trim() && form.idp_x509_cert?.trim()));
    }
    return Boolean(form.idp_entity_id?.trim() && form.idp_x509_cert?.trim());
  }, [setupMode, form.idp_metadata_url, form.idp_entity_id, form.idp_x509_cert]);

  const formDirty = savedForm != null && JSON.stringify(form) !== savedForm;
  const canSave =
    !saving &&
    Boolean((form.sp_entity_id || defaultSpEntityId).trim()) &&
    Boolean((form.sp_acs_url || defaultAcsUrl).trim()) &&
    idpReady;

  const handleFetchMetadata = async () => {
    if (!form.idp_metadata_url?.trim()) return;
    setFetching(true);
    setMessage(null);
    try {
      const res = await ssoAPI.fetchMetadataFromURL(form.idp_metadata_url.trim());
      const content: string = res.data?.data?.content || '';
      if (!content || !content.includes('EntityDescriptor')) {
        throw new Error('URL did not return a valid IdP metadata document.');
      }
      const entityId = parseEntityIdFromMetadata(content);
      const cert = parseCertFromMetadata(content);
      const ssoLoginUrl = parseSsoLoginUrlFromMetadata(content);
      setFetched({ entityId, cert });
      setForm((f) => ({
        ...f,
        idp_entity_id: entityId || f.idp_entity_id || '',
        idp_x509_cert: cert || f.idp_x509_cert || '',
        idp_sso_login_url: ssoLoginUrl || f.idp_sso_login_url || '',
      }));
      setMessage({
        type: 'success',
        text: 'Metadata loaded. Review the values below, then save.',
      });
    } catch (err: unknown) {
      const text =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err instanceof Error ? err.message : 'Could not load metadata from that URL.');
      setMessage({ type: 'error', text });
    } finally {
      setFetching(false);
    }
  };

  const handleApplyCredentials = async () => {
    if (!defaultSpEntityId.trim() || !credClientId.trim() || !credClientSecret.trim()) {
      setMessage({ type: 'error', text: 'Client ID and Client Secret are required.' });
      return;
    }
    if (credClientSecret.includes('•')) {
      setMessage({ type: 'error', text: 'Enter the real client secret (not the masked placeholder).' });
      return;
    }
    setApplyingCredentials(true);
    setMessage(null);
    try {
      const discovered = await enterpriseSamlLoginAPI.discoverFromCredentials({
        entity_id: defaultSpEntityId.trim(),
        client_id: credClientId.trim(),
        client_secret: credClientSecret.trim(),
        sp_acs_url: form.sp_acs_url?.trim() || defaultAcsUrl,
      });
      setForm((f) => ({
        ...f,
        sp_entity_id: defaultSpEntityId,
        sp_acs_url: discovered.sp_acs_url || defaultAcsUrl,
        idp_metadata_url: discovered.idp_metadata_url,
        idp_entity_id: discovered.idp_entity_id,
        idp_x509_cert: discovered.idp_x509_cert,
        idp_sso_login_url: discovered.idp_sso_login_url?.trim() || '',
        want_response_signed: discovered.want_response_signed,
      }));
      setSetupMode('metadata');
      setFetched({ entityId: discovered.idp_entity_id, cert: discovered.idp_x509_cert });
      setMessage({
        type: 'success',
        text: discovered.idp_sso_login_url
          ? 'Configuration applied. IdP sign-in URL is set for the login page. Register SP values in Entra, then save.'
          : 'Configuration applied. Register SP values in your IdP, then save.',
      });
    } catch (err: unknown) {
      const text =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err instanceof Error ? err.message : 'Could not apply credentials.');
      setMessage({ type: 'error', text });
    } finally {
      setApplyingCredentials(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const useMetadata = setupMode === 'metadata' || setupMode === 'credentials';
      const saved = await enterpriseSamlLoginAPI.save({
        // Always persist SP URLs from the current tenant origin (never stale azurewebsites ACS).
        sp_entity_id: defaultSpEntityId.trim(),
        sp_acs_url: defaultAcsUrl.trim(),
        idp_metadata_url: useMetadata ? form.idp_metadata_url?.trim() || null : null,
        idp_entity_id: form.idp_entity_id?.trim() || null,
        idp_x509_cert: form.idp_x509_cert?.trim() || null,
        idp_x509_cert_extra: form.idp_x509_cert_extra?.trim() || null,
        idp_sso_login_url: sanitizeIdpSignInUrl(form.idp_sso_login_url) || null,
        frontend_redirect_url: tenantBase || form.frontend_redirect_url?.trim() || null,
        accepted_clock_skew_ms: form.accepted_clock_skew_ms,
        want_assertions_signed: form.want_assertions_signed,
        want_response_signed: form.want_response_signed,
      });
      const next = {
        ...saved,
        sp_entity_id: defaultSpEntityId,
        sp_acs_url: defaultAcsUrl,
        idp_metadata_url: saved.idp_metadata_url ?? '',
        idp_entity_id: saved.idp_entity_id ?? '',
        idp_x509_cert: saved.idp_x509_cert ?? '',
        idp_x509_cert_extra: saved.idp_x509_cert_extra ?? '',
        idp_sso_login_url: saved.idp_sso_login_url ? sanitizeIdpSignInUrl(saved.idp_sso_login_url) : '',
        frontend_redirect_url: tenantBase || saved.frontend_redirect_url || '',
      };
      setForm(next);
      setSavedForm(JSON.stringify(next));
      setMessage({
        type: 'success',
        text:
          'SSO settings saved. Register this Entity ID and ACS URL in Okta (or your IdP), then Sign in with SSO will work.',
      });
    } catch (err: unknown) {
      const text =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Save failed. Check values and try again.';
      setMessage({ type: 'error', text });
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text?: string) => {
    if (!text) return;
    try {
      navigator.clipboard?.writeText(text);
    } catch {
      /* ignore */
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, color: 'text.secondary' }}>
        <CircularProgress size={18} />
        <Typography variant="body2">Loading SSO settings…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 880 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.2 }}>SSO login</Typography>
          <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', mt: 0.25 }}>
            One configuration for your identity provider — metadata URL, application credentials, or manual trust.
          </Typography>
        </Box>
        <Chip
          icon={idpReady ? <CheckIcon sx={{ fontSize: '0.9rem !important' }} /> : <InfoIcon sx={{ fontSize: '0.9rem !important' }} />}
          label={formDirty ? (idpReady ? 'Ready to save' : 'Unsaved changes') : 'Saved'}
          size="small"
          color={formDirty ? (idpReady ? 'success' : 'warning') : 'default'}
          variant={formDirty && idpReady ? 'filled' : 'outlined'}
          sx={{ height: 24, fontSize: UI_TOKENS.text.helper, fontWeight: 600 }}
        />
      </Stack>

      {message && (
        <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 2, fontSize: UI_TOKENS.text.bodySm }}>
          {message.text}
        </Alert>
      )}

      <Stack spacing={2}>
        <SectionCard
          icon={<ShieldIcon sx={{ fontSize: 18 }} />}
          title="Application (service provider)"
          subtitle="Register these in your IdP SAML application."
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Entity ID (audience)"
                placeholder="https://your-tenant.example.com/saml/sp"
                fullWidth
                required
                size="small"
                disabled
                value={defaultSpEntityId || form.sp_entity_id}
                helperText="TurboIAM Entity ID (your app / SP audience) — not Okta. Register this exact value in your IdP."
                InputProps={{
                  readOnly: true,
                  endAdornment: (defaultSpEntityId || form.sp_entity_id) ? (
                    <InputAdornment position="end">
                      <Tooltip title="Copy">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(defaultSpEntityId || form.sp_entity_id)}
                            tabIndex={-1}
                          >
                            <CopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Assertion consumer URL (ACS)"
                placeholder="https://your-tenant.example.com/api/v1/auth/saml/acs"
                fullWidth
                required
                size="small"
                disabled
                value={defaultAcsUrl || form.sp_acs_url}
                helperText="TurboIAM callback URL (your app / SP) — not Okta. Auto-generated from your tenant URL. Register this exact ACS in your IdP."
                InputProps={{
                  readOnly: true,
                  endAdornment: (defaultAcsUrl || form.sp_acs_url) ? (
                    <InputAdornment position="end">
                      <Tooltip title="Copy">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(defaultAcsUrl || form.sp_acs_url)}
                            tabIndex={-1}
                          >
                            <CopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard
          icon={<IdpIcon sx={{ fontSize: 18 }} />}
          title="Identity provider"
          subtitle="Choose how to connect your IdP."
          right={
            <Tabs
              value={setupMode}
              onChange={(_, v: SetupMode) => {
                setSetupMode(v);
                setFetched(null);
              }}
              sx={{
                minHeight: 32,
                '& .MuiTab-root': {
                  minHeight: 32,
                  py: 0.5,
                  px: 1.25,
                  fontSize: UI_TOKENS.text.buttonSm,
                  textTransform: 'none',
                },
              }}
            >
              <Tab value="metadata" label="Metadata" icon={<LinkIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab value="credentials" label="Credentials" icon={<KeyIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab value="manual" label="Manual" icon={<KeyIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
            </Tabs>
          }
        >
          {setupMode === 'metadata' && (
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                <TextField
                  label="IdP metadata URL"
                  placeholder="https://your-idp.example.com/.../metadata"
                  fullWidth
                  size="small"
                  value={form.idp_metadata_url}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, idp_metadata_url: e.target.value }));
                    setFetched(null);
                  }}
                  helperText="Paste the metadata URL from your IdP SAML application."
                  required
                />
                <Button
                  variant="contained"
                  onClick={handleFetchMetadata}
                  disabled={fetching || !form.idp_metadata_url?.trim()}
                  startIcon={
                    fetching ? <CircularProgress size={14} color="inherit" /> : <FetchIcon sx={{ fontSize: 18 }} />
                  }
                  sx={{ minWidth: 130, textTransform: 'none', fontSize: UI_TOKENS.text.buttonSm }}
                >
                  {fetching ? 'Loading…' : 'Load metadata'}
                </Button>
              </Stack>
              {(fetched || form.idp_entity_id || form.idp_x509_cert) && (
                <Card variant="outlined" sx={{ bgcolor: 'grey.50', borderRadius: 1.5 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, fontWeight: 700, mb: 1 }}>
                      Resolved from metadata
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <TextField
                          label="IdP entity ID"
                          fullWidth
                          size="small"
                          value={form.idp_entity_id || ''}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="IdP signing certificate"
                          fullWidth
                          size="small"
                          multiline
                          minRows={form.idp_x509_cert ? 4 : 2}
                          value={form.idp_x509_cert || ''}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Stack>
          )}

          {setupMode === 'credentials' && (
            <Stack spacing={1.5} component="form" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
              <Alert severity="info" sx={{ fontSize: UI_TOKENS.text.bodySm }}>
                Enter your Entra app registration values (Application ID and client secret). These are not your
                Provysn login username or password. The secret is only used to resolve trust details and is not
                stored.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Entity ID (SP identifier)"
                    fullWidth
                    size="small"
                    required
                    disabled
                    value={defaultSpEntityId}
                    helperText="Auto-generated from your tenant URL. Use this value when registering the SAML app in Entra."
                    InputProps={{
                      readOnly: true,
                      endAdornment: defaultSpEntityId ? (
                        <InputAdornment position="end">
                          <Tooltip title="Copy">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(defaultSpEntityId)}
                                tabIndex={-1}
                              >
                                <CopyIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Application (client) ID"
                    fullWidth
                    size="small"
                    required
                    value={credClientId}
                    onChange={(e) => setCredClientId(e.target.value)}
                    onFocus={() => setCredFieldsUnlocked(true)}
                    inputProps={{
                      autoComplete: 'off',
                      name: 'turboiam-idp-application-id',
                      'data-lpignore': 'true',
                      'data-1p-ignore': 'true',
                    }}
                    InputProps={{ readOnly: !credFieldsUnlocked }}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    helperText="From Entra → App registrations → Overview."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Client secret"
                    fullWidth
                    size="small"
                    required
                    type="password"
                    value={credClientSecret}
                    onChange={(e) => setCredClientSecret(e.target.value)}
                    onFocus={() => setCredFieldsUnlocked(true)}
                    inputProps={{
                      autoComplete: 'new-password',
                      name: 'turboiam-idp-client-secret',
                      'data-lpignore': 'true',
                      'data-1p-ignore': 'true',
                    }}
                    InputProps={{ readOnly: !credFieldsUnlocked }}
                    helperText="Required to resolve metadata and certificates."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    disabled={
                      applyingCredentials ||
                      !defaultSpEntityId.trim() ||
                      !credClientId.trim() ||
                      !credClientSecret.trim()
                    }
                    onClick={handleApplyCredentials}
                    startIcon={
                      applyingCredentials ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <FetchIcon sx={{ fontSize: 18 }} />
                      )
                    }
                    sx={{ textTransform: 'none' }}
                  >
                    {applyingCredentials ? 'Applying…' : 'Apply configuration'}
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          )}

          {setupMode === 'manual' && (
            <Stack spacing={1.5}>
              <TextField
                label="IdP entity ID"
                fullWidth
                required
                size="small"
                value={form.idp_entity_id}
                onChange={(e) => setForm((f) => ({ ...f, idp_entity_id: e.target.value }))}
              />
              <TextField
                label="IdP signing certificate (PEM)"
                fullWidth
                required
                multiline
                minRows={5}
                size="small"
                value={form.idp_x509_cert}
                onChange={(e) => setForm((f) => ({ ...f, idp_x509_cert: e.target.value }))}
                placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
              />
            </Stack>
          )}

          <Divider sx={{ my: 2 }} />
          <TextField
            label="Additional IdP certificates (optional)"
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={form.idp_x509_cert_extra}
            onChange={(e) => setForm((f) => ({ ...f, idp_x509_cert_extra: e.target.value }))}
            helperText="Separate multiple PEM blocks with a line containing only |||."
          />
        </SectionCard>

        <SectionCard
          icon={<LinkIcon sx={{ fontSize: 18 }} />}
          title="Sign-in redirect"
          subtitle="Optional direct IdP URL for the login page SSO button."
        >
          <TextField
            label="IdP sign-in URL (optional)"
            fullWidth
            size="small"
            value={form.idp_sso_login_url}
            onChange={(e) => setForm((f) => ({ ...f, idp_sso_login_url: e.target.value }))}
            placeholder={defaultSpLoginUrl ? `Leave empty to use ${defaultSpLoginUrl}` : 'Leave empty for automatic SAML redirect'}
            helperText={
              isBareAzureSamlEndpoint(form.idp_sso_login_url)
                ? 'This Azure SAML endpoint cannot be opened directly. Apply credentials again or use the My Apps launcher URL.'
                : form.idp_sso_login_url
                  ? 'Used by the Sign in with SSO button on the login page.'
                  : 'For Azure, Apply configuration sets the My Apps launcher URL automatically. For Okta, paste the IdP SSO URL or leave empty for automatic SAML.'
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
        </SectionCard>

        <SectionCard
          icon={<SecurityIcon sx={{ fontSize: 18 }} />}
          title="Security"
          subtitle="Signature checks on incoming SAML responses."
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.want_assertions_signed}
                  onChange={(e) => setForm((f) => ({ ...f, want_assertions_signed: e.target.checked }))}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>
                    Require signed assertions
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.want_response_signed}
                  onChange={(e) => setForm((f) => ({ ...f, want_response_signed: e.target.checked }))}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>
                    Require signed responses
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </SectionCard>

        <SectionCard
          icon={<TuneIcon sx={{ fontSize: 18 }} />}
          title="Advanced"
          subtitle="Optional redirect and timing controls."
          right={
            <Button
              size="small"
              onClick={() => setShowAdvanced((s) => !s)}
              endIcon={showAdvanced ? <CollapseIcon /> : <ExpandIcon />}
              sx={{ textTransform: 'none', fontSize: UI_TOKENS.text.buttonSm }}
            >
              {showAdvanced ? 'Hide' : 'Show'}
            </Button>
          }
        >
          <Collapse in={showAdvanced} unmountOnExit>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Frontend URL after sign-in (optional)"
                  fullWidth
                  size="small"
                  value={form.frontend_redirect_url}
                  onChange={(e) => setForm((f) => ({ ...f, frontend_redirect_url: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Clock skew (ms)"
                  type="number"
                  fullWidth
                  size="small"
                  value={form.accepted_clock_skew_ms}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, accepted_clock_skew_ms: Number(e.target.value) || 0 }))
                  }
                  inputProps={{ min: 0, max: 86400000 }}
                />
              </Grid>
            </Grid>
          </Collapse>
        </SectionCard>

        <Box
          sx={{
            position: 'sticky',
            bottom: -1,
            zIndex: 1,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary' }}>
            {formDirty ? (canSave ? 'Ready to save.' : 'Finish the metadata URL before saving.') : 'No unsaved changes.'}
            {form.updated_by_name?.trim()
              ? ` Last change recorded for ${form.updated_by_name.trim()}.`
              : form.updated_by
                ? ' Last change recorded for a user whose name is not on file.'
                : ''}
          </Typography>
          <Button
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!canSave}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default SamlLoginSettings;
